import { useState } from 'react';
import Alert from '@mui/material/Alert';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import TablePagination from '@mui/material/TablePagination';
import Typography from '@mui/material/Typography';
import { Link as RouterLink } from 'react-router-dom';

import { useEnviosDeEmail } from '../../shared/api/queries.ts';
import type { Cliente } from '../../shared/api/tipos.ts';
import { competenciaPorExtenso } from '../../shared/format.ts';
import { useSessao } from '../auth/sessao.tsx';
import { DialogoRevogarLinks } from '../envios/DialogoRevogarLinks.tsx';
import { TabelaDeEnvios } from '../envios/TabelaDeEnvios.tsx';
import { SeletorDeCliente } from './SeletorDeCliente.tsx';

/**
 * O que já saiu do mês que está sendo olhado.
 *
 * É a mesma tabela de `/envios-de-email`, recortada pelo contexto da tela: o
 * mês escolhido nas abas e, quando houver, o cliente selecionado. A pergunta
 * "e o que a gente já mandou deste mês?" aparece no meio do fechamento, e
 * mandar quem fecha para outra tela — onde teria de reencontrar a loja e
 * redescobrir o mês — é perder o contexto que ele já tem aqui.
 *
 * O recorte vem de cima e não se edita aqui: dois lugares para escolher o mês
 * na mesma tela fariam a aba responder por um mês e as outras por outro.
 */
export function HistoricoDeEnvios({
  competencia,
  clientes,
  carregandoClientes,
  empresaId,
  aoEscolherCliente,
}: {
  competencia: string;
  clientes: readonly Cliente[];
  carregandoClientes: boolean;
  empresaId: string | null;
  aoEscolherCliente: (empresaId: string | null) => void;
}) {
  const { usuario } = useSessao();
  const podeRevogar = usuario?.papel === 'administrador';

  const [pagina, setPagina] = useState(0);
  const [porPagina, setPorPagina] = useState(25);
  const [revogando, setRevogando] = useState<{
    empresaId: string;
    razaoSocial: string;
  } | null>(null);
  const [revogados, setRevogados] = useState<number | null>(null);

  const { data, isPending, isError, error, isFetching } = useEnviosDeEmail(
    { competencia, empresaId: empresaId ?? undefined },
    { limite: porPagina, deslocamento: pagina * porPagina },
  );

  const itens = data?.itens ?? [];
  const total = data?.total ?? 0;

  return (
    <Stack spacing={2}>
      <Card>
        <CardContent>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            sx={{ alignItems: { md: 'flex-start' } }}
          >
            <SeletorDeCliente
              clientes={clientes}
              carregando={carregandoClientes}
              empresaId={empresaId}
              aoEscolher={(escolhido) => {
                setPagina(0);
                aoEscolherCliente(escolhido);
              }}
              ajuda="Em branco: todos os clientes do mês."
            />

            <Stack sx={{ pt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Mostrando os envios de <strong>{competenciaPorExtenso(competencia)}</strong>
                {empresaId === null ? ' de todos os clientes' : ' deste cliente'}. O mês vem da
                aba Clientes — é o mesmo das outras abas.
              </Typography>
              {/* A pergunta "mandaram as notas da loja tal?" nem sempre vem
                  com um mês junto, e esta aba sempre tem um. O log inteiro
                  continua existindo, e se chega a ele por aqui. */}
              <Link component={RouterLink} to="/envios-de-email" variant="body2">
                Ver o log completo, de todos os meses
              </Link>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <TabelaDeEnvios
            itens={itens}
            carregando={isPending}
            recarregando={isFetching}
            erro={isError ? error : undefined}
            vazio="Nada saiu deste mês ainda. O fechamento automático roda no dia 5, e o envio manual sai da tela de arquivos fiscais."
            aoRevogar={podeRevogar ? setRevogando : undefined}
          />
        </CardContent>

        {total > 0 && (
          <TablePagination
            component="div"
            count={total}
            page={pagina}
            onPageChange={(_, nova) => setPagina(nova)}
            rowsPerPage={porPagina}
            onRowsPerPageChange={(evento) => {
              setPorPagina(Number(evento.target.value));
              setPagina(0);
            }}
            rowsPerPageOptions={[25, 50, 100]}
            labelRowsPerPage="Por página"
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
          />
        )}
      </Card>

      {revogados !== null && (
        <Alert severity="success" onClose={() => setRevogados(null)}>
          {revogados === 1 ? '1 link revogado.' : `${revogados} links revogados.`}
        </Alert>
      )}

      <DialogoRevogarLinks
        cliente={revogando}
        aoFechar={() => setRevogando(null)}
        aoRevogado={setRevogados}
      />
    </Stack>
  );
}
